import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Search,
  Phone,
  Mail,
  Clock,
  Trash2,
  Download,
  RefreshCw,
  StickyNote,
  ChevronRight,
  Filter,
  Sparkles,
  PhoneCall,
  UserCheck,
  AlertTriangle,
  X,
  MessageCircle,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  new: {
    label: "New",
    color: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-800",
    dot: "bg-blue-500",
  },
  contacted: {
    label: "Contacted",
    color: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800",
    dot: "bg-amber-500",
  },
  in_discussion: {
    label: "Discussion",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-400 dark:border-indigo-800",
    dot: "bg-indigo-500",
  },
  converted: {
    label: "Converted",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-800",
    dot: "bg-emerald-500",
  },
  archived: {
    label: "Archived",
    color: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700",
    dot: "bg-slate-400",
  },
};

export default function ContactLeadsPage() {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Note Modal
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  // Delete Confirmation
  const [deleteConfirmLead, setDeleteConfirmLead] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();

      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads/contact`, { params, withCredentials: true }),
        axios.get(`${API}/leads/contact/stats`, { withCredentials: true }),
      ]);

      setLeads(leadsRes.data?.leads || []);
      setStats(statsRes.data || { total: 0, new: 0, contacted: 0, converted: 0, today: 0 });
    } catch (err) {
      console.error("Error loading contact leads:", err);
      toast.error("Failed to load contact leads");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/contact/${leadId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setLeads((prev) =>
        prev.map((l) => (l.lead_id === leadId ? { ...l, status: newStatus } : l))
      );
      const statsRes = await axios.get(`${API}/leads/contact/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (err) {
      console.error("Error updating lead status:", err);
      toast.error("Failed to update status");
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    setAddingNote(true);
    try {
      const res = await axios.post(
        `${API}/leads/contact/${selectedLead.lead_id}/notes`,
        { note: noteText.trim() },
        { withCredentials: true }
      );
      toast.success("Note added successfully");
      setNoteText("");
      setSelectedLead(res.data);
      setLeads((prev) =>
        prev.map((l) => (l.lead_id === selectedLead.lead_id ? res.data : l))
      );
    } catch (err) {
      console.error("Error adding note:", err);
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!deleteConfirmLead) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/leads/contact/${deleteConfirmLead.lead_id}`, { withCredentials: true });
      toast.success("Lead deleted successfully");
      setLeads((prev) => prev.filter((l) => l.lead_id !== deleteConfirmLead.lead_id));
      setDeleteConfirmLead(null);
      const statsRes = await axios.get(`${API}/leads/contact/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (err) {
      console.error("Error deleting lead:", err);
      toast.error("Failed to delete lead");
    } finally {
      setDeleting(false);
    }
  };

  const exportCSV = () => {
    if (!leads.length) {
      toast.error("No leads to export");
      return;
    }
    const headers = ["Lead ID", "Name", "Email", "Phone", "Status", "Message", "Created At"];
    const rows = leads.map((l) => [
      l.lead_id,
      `"${l.name || `${l.first_name || ""} ${l.last_name || ""}`.trim()}"`,
      `"${l.email || ""}"`,
      `"${l.phone || ""}"`,
      l.status || "new",
      `"${(l.message || "").replace(/"/g, '""')}"`,
      l.created_at || "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `jamadhan_contact_leads_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exported leads to CSV");
  };

  return (
    <DashboardLayout title="Contact Leads CRM">
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans">
        
        {/* ===================== TOP HEADER & BREADCRUMB ===================== */}
        <div className={`p-6 rounded-2xl border transition-all ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-lg"
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1.5">
                <span>Operations</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className={isLight ? "text-slate-900 font-semibold" : "text-zinc-200 font-semibold"}>
                  Admissions & Inquiries CRM
                </span>
              </div>
              <div className="flex items-center gap-3">
                <h1 className={`text-2xl md:text-3xl font-bold tracking-tight ${
                  isLight ? "text-slate-900" : "text-white"
                }`}>
                  Contact Inquiries & Admissions
                </h1>
                <Badge variant="outline" className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                  isLight ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-blue-950/60 text-blue-400 border-blue-800"
                }`}>
                  Live Inbound Stream
                </Badge>
              </div>
              <p className={`text-xs md:text-sm mt-1.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                Real-time admissions and course inquiries submitted from the official Jamadhan portal.
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={exportCSV}
                className={`h-9 text-xs font-medium rounded-xl border ${
                  isLight
                    ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                }`}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
              <Button
                size="sm"
                onClick={fetchLeads}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-3.5 rounded-xl shadow-xs transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Refresh Stream
              </Button>
            </div>
          </div>
        </div>

        {/* ===================== METRIC STATS CARDS ===================== */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
          {/* Total Inquiries */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  Total Inquiries
                </p>
                <p className={`text-2xl font-bold font-mono tracking-tight mt-1 ${isLight ? "text-slate-900" : "text-white"}`}>
                  {stats.total}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">All-time leads</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300"
              }`}>
                <Users className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* New / Unread */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-blue-700" : "text-blue-400"}`}>
                  New / Unread
                </p>
                <p className={`text-2xl font-bold font-mono tracking-tight mt-1 ${isLight ? "text-blue-700" : "text-blue-400"}`}>
                  {stats.new}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Requires response</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-blue-50 text-blue-600 border border-blue-100" : "bg-blue-950/60 text-blue-400 border border-blue-900"
              }`}>
                <Sparkles className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Today's Leads */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-amber-700" : "text-amber-400"}`}>
                  Today's Inquiries
                </p>
                <p className={`text-2xl font-bold font-mono tracking-tight mt-1 ${isLight ? "text-amber-700" : "text-amber-400"}`}>
                  {stats.today}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Last 24 hours</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-amber-50 text-amber-600 border border-amber-100" : "bg-amber-950/60 text-amber-400 border border-amber-900"
              }`}>
                <Clock className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* In Discussion */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-indigo-700" : "text-indigo-400"}`}>
                  In Discussion
                </p>
                <p className={`text-2xl font-bold font-mono tracking-tight mt-1 ${isLight ? "text-slate-900" : "text-white"}`}>
                  {stats.contacted}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Active follow-up</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-indigo-50 text-indigo-600 border border-indigo-100" : "bg-indigo-950/60 text-indigo-400 border border-indigo-900"
              }`}>
                <PhoneCall className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Converted */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-emerald-700" : "text-emerald-400"}`}>
                  Converted
                </p>
                <p className={`text-2xl font-bold font-mono tracking-tight mt-1 ${isLight ? "text-emerald-700" : "text-emerald-400"}`}>
                  {stats.converted}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">Enrolled students</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-emerald-950/60 text-emerald-400 border border-emerald-900"
              }`}>
                <UserCheck className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ===================== FILTER & SEARCH BAR ===================== */}
        <div className={`p-3.5 rounded-2xl border transition-all flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"
        }`}>
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 text-xs custom-scrollbar">
            {[
              { id: "all", label: "All Leads" },
              { id: "new", label: "New" },
              { id: "contacted", label: "Contacted" },
              { id: "in_discussion", label: "Discussion" },
              { id: "converted", label: "Converted" },
              { id: "archived", label: "Archived" },
            ].map((tab) => {
              const isSelected = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-xl font-medium transition-all shrink-0 text-xs ${
                    isSelected
                      ? isLight
                        ? "bg-slate-900 text-white font-semibold shadow-xs"
                        : "bg-white text-zinc-900 font-semibold shadow-xs"
                      : isLight
                        ? "bg-slate-50 text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200/60"
                        : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 border border-zinc-700/60"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-8 pr-7 h-8 text-xs rounded-xl ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white"
                  : "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              }`}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ===================== LEADS ROSTER (DESKTOP TABLE + MOBILE CARDS) ===================== */}
        {loading ? (
          <div className="py-20 text-center">
            <RefreshCw className="w-7 h-7 animate-spin mx-auto text-emerald-600 mb-3" />
            <p className={`text-sm ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
              Syncing admission inquiries and leads...
            </p>
          </div>
        ) : leads.length === 0 ? (
          <div className={`py-16 text-center rounded-2xl border ${
            isLight ? "bg-white border-slate-200" : "bg-zinc-900 border-zinc-800"
          }`}>
            <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className={`text-sm font-semibold ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
              No inquiries found
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {search ? "No leads matched your search query." : "Inquiries from the Contact Us page will stream here in real time."}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View (hidden md:block) */}
            <div className={`hidden md:block rounded-2xl border overflow-hidden ${
              isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
            }`}>
              <div className="overflow-x-auto">
                <Table className="w-full text-left text-xs">
                  <TableHeader className={isLight ? "bg-slate-50/80" : "bg-zinc-800/80"}>
                    <TableRow className={isLight ? "border-slate-200/80" : "border-zinc-700"}>
                      <TableHead className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Lead / Name
                      </TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Contact Channels
                      </TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Inquiry Message
                      </TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Status
                      </TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Received At
                      </TableHead>
                      <TableHead className="py-3 px-4 text-right font-semibold text-[11px] uppercase tracking-wider text-slate-500">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className={`divide-y ${isLight ? "divide-slate-100" : "divide-zinc-800"}`}>
                    {leads.map((lead) => {
                      const statusCfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                      const cleanPhone = (lead.phone || "").replace(/[^0-9]/g, "");
                      const waLink = cleanPhone
                        ? `https://wa.me/${cleanPhone.startsWith("91") ? cleanPhone : `91${cleanPhone}`}?text=Hello%20${encodeURIComponent(lead.first_name || lead.name || "")},%20this%20is%20JamaDhan%20Stock%20Market%20Institute.`
                        : null;

                      return (
                        <TableRow
                          key={lead.lead_id}
                          className={`transition-colors ${isLight ? "hover:bg-slate-50/70" : "hover:bg-zinc-800/40"}`}
                        >
                          {/* Name & Source */}
                          <TableCell className="py-3.5 px-4 align-top">
                            <p className={`font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                              {lead.name || `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "Anonymous"}
                            </p>
                            <span className="inline-block text-[10px] font-mono text-slate-400 mt-0.5">
                              Source: {lead.source || "contact_page"}
                            </span>
                          </TableCell>

                          {/* Contact Info */}
                          <TableCell className="py-3.5 px-4 align-top">
                            <div className="space-y-1">
                              {lead.phone && (
                                <div className="flex items-center gap-2">
                                  <a
                                    href={`tel:${lead.phone}`}
                                    className="font-mono text-emerald-700 hover:underline flex items-center gap-1 font-medium"
                                  >
                                    <Phone className="w-3 h-3 text-emerald-600" /> {lead.phone}
                                  </a>
                                  {waLink && (
                                    <a
                                      href={waLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Chat on WhatsApp"
                                      className="p-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                                    >
                                      <MessageCircle className="w-3 h-3" />
                                    </a>
                                  )}
                                </div>
                              )}
                              {lead.email && (
                                <a
                                  href={`mailto:${lead.email}`}
                                  className="text-slate-500 hover:text-slate-800 flex items-center gap-1 font-mono text-[11px]"
                                >
                                  <Mail className="w-3 h-3 text-slate-400" /> {lead.email}
                                </a>
                              )}
                            </div>
                          </TableCell>

                          {/* Message & Note Pill */}
                          <TableCell className="py-3.5 px-4 align-top max-w-sm">
                            <p className={`leading-relaxed line-clamp-2 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
                              {lead.message || <span className="italic text-slate-400">No message provided</span>}
                            </p>
                            {lead.notes && lead.notes.length > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setNoteDialogOpen(true);
                                }}
                                className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:underline mt-1 font-medium"
                              >
                                <StickyNote className="w-3 h-3" /> {lead.notes.length} internal note{lead.notes.length > 1 ? "s" : ""}
                              </button>
                            )}
                          </TableCell>

                          {/* Status Dropdown */}
                          <TableCell className="py-3.5 px-4 align-top">
                            <Select
                              value={lead.status || "new"}
                              onValueChange={(val) => handleStatusChange(lead.lead_id, val)}
                            >
                              <SelectTrigger className={`h-7 text-xs font-semibold rounded-lg border ${statusCfg.color} bg-white`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="in_discussion">Discussion</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>

                          {/* Date */}
                          <TableCell className="py-3.5 px-4 align-top font-mono text-slate-500 text-[11px]">
                            {lead.created_at
                              ? new Date(lead.created_at).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="py-3.5 px-4 align-top text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Add internal note"
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setNoteDialogOpen(true);
                                }}
                                className="h-7 w-7 p-0 text-slate-500 hover:text-slate-900"
                              >
                                <StickyNote className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Delete lead"
                                onClick={() => setDeleteConfirmLead(lead)}
                                className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mobile Card View (block md:hidden) - Prevents Awkward Horizontal Scroll! */}
            <div className="block md:hidden space-y-3">
              {leads.map((lead) => {
                const statusCfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                const cleanPhone = (lead.phone || "").replace(/[^0-9]/g, "");
                const waLink = cleanPhone
                  ? `https://wa.me/${cleanPhone.startsWith("91") ? cleanPhone : `91${cleanPhone}`}?text=Hello%20${encodeURIComponent(lead.first_name || lead.name || "")},%20this%20is%20JamaDhan%20Stock%20Market%20Institute.`
                  : null;

                return (
                  <div
                    key={lead.lead_id}
                    className={`p-4 rounded-2xl border space-y-3 ${
                      isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-bold text-sm text-slate-900">
                          {lead.name || `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "Anonymous"}
                        </h4>
                        <span className="text-[10px] font-mono text-slate-400">
                          {lead.created_at
                            ? new Date(lead.created_at).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>

                      <Select
                        value={lead.status || "new"}
                        onValueChange={(val) => handleStatusChange(lead.lead_id, val)}
                      >
                        <SelectTrigger className={`h-7 text-[11px] font-semibold rounded-lg border ${statusCfg.color} bg-white shrink-0`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="in_discussion">Discussion</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Quick Call & WhatsApp Chips */}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {lead.phone && (
                        <a
                          href={`tel:${lead.phone}`}
                          className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-mono font-semibold border border-emerald-200 flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </a>
                      )}
                      {waLink && (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200 flex items-center gap-1"
                        >
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                      {lead.email && (
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-[11px] font-mono text-slate-500 hover:text-slate-800 flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                      )}
                    </div>

                    {/* Message Box */}
                    {lead.message && (
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700 leading-relaxed">
                        {lead.message}
                      </div>
                    )}

                    {/* Card Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setNoteDialogOpen(true);
                        }}
                        className="text-xs text-indigo-600 hover:underline font-medium flex items-center gap-1"
                      >
                        <StickyNote className="w-3 h-3" />
                        {lead.notes?.length ? `${lead.notes.length} internal notes` : "Add note"}
                      </button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmLead(lead)}
                        className="h-7 text-xs text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ===================== FOLLOW-UP NOTES MODAL ===================== */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="max-w-md bg-white border-slate-200 text-slate-900 rounded-3xl p-6">
            <DialogHeader>
              <DialogTitle className="text-base font-bold flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-emerald-600" />
                Internal Follow-Up Notes: {selectedLead?.name || selectedLead?.first_name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2 max-h-56 overflow-y-auto overscroll-contain pr-1 custom-scrollbar">
                {selectedLead?.notes && selectedLead.notes.length > 0 ? (
                  selectedLead.notes.map((n, idx) => (
                    <div key={n.note_id || idx} className="p-3 rounded-xl border border-slate-100 bg-slate-50 text-xs space-y-1">
                      <p className="text-slate-800 leading-relaxed">{n.text}</p>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 font-mono">
                        <span>{n.author_name || "Staff Counselor"}</span>
                        <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 italic py-3 text-center">No internal notes added yet.</p>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100">
                <label className="text-xs font-semibold text-slate-700">Add Counselor Follow-Up Note</label>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="e.g. Spoke on call, invited for weekend seminar demo class..."
                  rows={3}
                  className="text-xs bg-slate-50 border-slate-200 text-slate-900 rounded-xl"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNoteDialogOpen(false)}
                className="text-xs rounded-xl"
              >
                Close
              </Button>
              <Button
                size="sm"
                disabled={addingNote || !noteText.trim()}
                onClick={handleAddNote}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl"
              >
                {addingNote ? "Saving..." : "Add Note"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===================== DELETE CONFIRMATION DIALOG ===================== */}
        <Dialog open={!!deleteConfirmLead} onOpenChange={(open) => !open && setDeleteConfirmLead(null)}>
          <DialogContent className="max-w-sm bg-white border-slate-200 text-slate-900 rounded-3xl p-6">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-rose-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600" /> Delete Lead Record?
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-600 leading-relaxed mt-1">
              Are you sure you want to delete the lead for <strong>{deleteConfirmLead?.name || deleteConfirmLead?.email}</strong>? This action cannot be reversed.
            </p>
            <DialogFooter className="gap-2 sm:gap-0 pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmLead(null)}
                className="text-xs rounded-xl"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={deleting}
                onClick={handleDeleteLead}
                className="bg-rose-600 hover:bg-rose-700 text-white font-medium text-xs rounded-xl"
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
